import { z } from 'zod';

// Email validation (RFC 5322 simplified)
const emailSchema = z.string()
  .email('Invalid email format')
  .min(5, 'Email must be at least 5 characters')
  .max(254, 'Email must be less than 254 characters')
  .toLowerCase();

// Phone validation (accepts international formats)
const phoneSchema = z.string()
  .regex(
    /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
    'Invalid phone format'
  )
  .min(10, 'Phone must be at least 10 characters')
  .max(20, 'Phone must be less than 20 characters')
  .optional()
  .or(z.literal(''));

// Name validation
const nameSchema = z.string()
  .min(1, 'Name is required')
  .max(100, 'Name must be less than 100 characters')
  .regex(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes');

const optionalNameSchema = nameSchema.optional().or(z.literal(''));

// Address validation
const addressSchema = z.object({
  street: z.string().min(1, 'Street address is required').max(200),
  city: z.string().min(1, 'City is required').max(100),
  state: z.string().min(2, 'State must be 2 characters').max(2),
  postal_code: z.string()
    .regex(/^[0-9]{5}(-[0-9]{4})?$|^[A-Za-z]{1,2}[0-9]{1,2}[A-Za-z]?\s?[0-9][A-Za-z]{2}$/, 'Invalid postal code format'),
  country: z.string().min(2, 'Country is required').max(100)
});

const optionalAddressSchema = addressSchema.optional();

// URL validation
const urlSchema = z.string()
  .url('Invalid URL format')
  .optional()
  .or(z.literal(''));

// Password validation - Updated to 12-character minimum with comprehensive security checks
import { validatePasswordStrength } from '@/utils/password-validation';

const passwordSchema = z
  .string()
  .min(1, 'Password is required')
  .refine(
    (password) => {
      const validation = validatePasswordStrength(password);
      return validation.isValid;
    },
    (password) => {
      const validation = validatePasswordStrength(password);
      return {
        message: validation.errors[0] || 'Password does not meet security requirements'
      };
    }
  );

// ============ PROFILE SCHEMAS ============

export const profileUpdateSchema = z.object({
  // Basic identity
  legal_first_name: optionalNameSchema,
  legal_last_name: optionalNameSchema,
  preferred_first_name: optionalNameSchema,
  
  // Contact information
  business_email: z.string().email('Invalid email format').optional().or(z.literal('')),
  personal_email: z.string().email('Invalid email format').optional().or(z.literal('')),
  business_phone: phoneSchema,
  personal_phone: phoneSchema,
  
  // Professional details
  position_title: z.string().max(100).optional().or(z.literal('')),
  role: z.string().optional().or(z.literal('')),
  
  // Profile details
  profile_image: z.string().url('Invalid image URL').optional().or(z.literal('')).or(z.null()),
  bio: z.string().max(500).optional().or(z.literal('')),
  join_date: z.string().optional().or(z.literal('')).or(z.null()),
  
  // Addresses
  home_address: z.any().optional(),
  work_address: z.any().optional(),
  
  // Business info
  company_name: z.string().max(200).optional().or(z.literal('')),
  industry: z.string().optional().or(z.literal('')),
  company_size: z.string().optional().or(z.literal('')),
  business_structure: z.string().optional().or(z.literal('')),
  goals: z.array(z.string()).optional(),
  
  // Additional
  connected_accounts: z.any().optional(),
  early_access: z.boolean().optional(),
  
  // Onboarding fields
  has_specific_business: z.string().optional().or(z.literal('')),
  successor_relationship: z.string().optional().or(z.literal('')),
  advisor_relationship: z.string().optional().or(z.literal('')),
  client_count: z.string().optional().or(z.literal('')),
  years_in_business: z.number().optional().or(z.null()),
}).passthrough().partial();

export type ProfileUpdateInput = z.infer<typeof profileUpdateSchema>;

export const contactInfoSchema = z.object({
  business_email: z.string().email('Invalid email format').optional().or(z.literal('')),
  personal_email: z.string().email('Invalid email format').optional().or(z.literal('')),
  business_phone: phoneSchema,
  personal_phone: phoneSchema,
}).strict().partial();

export type ContactInfoInput = z.infer<typeof contactInfoSchema>;

export const addressUpdateSchema = z.object({
  type: z.enum(['home_address', 'work_address']),
  address: addressSchema
});

export type AddressUpdateInput = z.infer<typeof addressUpdateSchema>;

// ============ AUTHENTICATION SCHEMAS ============

export const signUpSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  first_name: optionalNameSchema,
  last_name: optionalNameSchema
}).strict();

export type SignUpInput = z.infer<typeof signUpSchema>;

export const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required')
}).strict();

export type SignInInput = z.infer<typeof signInSchema>;

export const resetPasswordSchema = z.object({
  email: emailSchema
}).strict();

export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const updatePasswordSchema = z.object({
  password: passwordSchema
}).strict();

export type UpdatePasswordInput = z.infer<typeof updatePasswordSchema>;

// ============ FILE UPLOAD SCHEMAS ============

export const fileUploadSchema = z.object({
  file: z.instanceof(File)
    .refine(
      (file) => file.size <= 5 * 1024 * 1024,
      'File size must be less than 5MB'
    )
    .refine(
      (file) => ['image/jpeg', 'image/png', 'image/webp'].includes(file.type),
      'File must be JPG, PNG, or WebP format'
    )
    .refine(
      (file) => {
        const validExtensions = ['jpg', 'jpeg', 'png', 'webp'];
        const ext = file.name.split('.').pop()?.toLowerCase();
        return ext && validExtensions.includes(ext);
      },
      'Invalid file extension'
    )
});

export type FileUploadInput = z.infer<typeof fileUploadSchema>;

// ============ VALIDATION HELPERS ============

export type ValidationResult<T> = 
  | { valid: true; data: T; errors?: never }
  | { valid: false; data?: never; errors: Record<string, string> };

export function validateInput<T>(schema: z.ZodSchema<T>, data: unknown): ValidationResult<T> {
  try {
    const validated = schema.parse(data);
    return { valid: true, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: Record<string, string> = {};
      error.errors.forEach((err) => {
        const path = err.path.join('.');
        errors[path] = err.message;
      });
      return { valid: false, errors };
    }
    return { valid: false, errors: { _root: 'Validation failed' } };
  }
}

export function getFirstError(errors: Record<string, string>): string {
  const firstKey = Object.keys(errors)[0];
  return errors[firstKey] || 'Validation failed';
}
